import React from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
} from '@mui/material';
import {
  Mic as MicIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Folder as FolderIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Article as ArticleIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  Cancel as CancelIcon,
  MenuBook as MenuBookIcon,
  QrCode2 as QrCodeIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { AppColors } from '../theme/colors';
import { switchToCorporateUser as switchToCorporateUserToken } from '../services/accessLinkService';
import { useAuth, useCompany } from '../hooks/useAuth';
import { getBoxTemplate } from '../constants/industryTemplates';

type AdminTab = 'voice' | 'thanks' | 'theme';

interface AdminDesktopSidebarProps {
  onTabChange?: (tab: AdminTab) => void;
}

/**
 * PC版用の管理者画面固定サイドバー
 */
export const AdminDesktopSidebar: React.FC<AdminDesktopSidebarProps> = ({ onTabChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { company } = useCompany(user?.uid || null);
  
  // 現在のタブを判定
  const currentTab = (searchParams.get('tab') as AdminTab) || 'voice';

  const handleTabClick = (tab: AdminTab) => {
    if (onTabChange) {
      onTabChange(tab);
    }
    // URLパラメータを更新
    navigate(`/admin?tab=${tab}`);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const switchToCorporateUser = async () => {
    // 切り替え中フラグを設定（App.tsxのProtectedRouteで検知される）
    sessionStorage.setItem('switching_user', 'true');

    try {
      // 以前は会社パスワードを取得して共有アカウントにログインし直していたが、
      // 会社パスワードは QR/URL 方式では不要になるため、サーバーが発行する
      // カスタムトークンでサインインする形に変更した。
      localStorage.removeItem('adminUserId');
      localStorage.removeItem('loginMethod');
      await switchToCorporateUserToken();

      // 認証状態が更新されるまで待機
      await new Promise(resolve => setTimeout(resolve, 1000));

      sessionStorage.removeItem('switching_user');
      // 切り替え完了フラグを設定（ホーム画面でスナックバーを表示するため）
      sessionStorage.setItem('user_switched', 'true');

      // ページ全体をリロードしてホーム画面へ（認証状態を確実に更新するため）
      window.location.href = '/home';
    } catch (error: any) {
      console.error('ユーザー切り替えエラー:', error);
      sessionStorage.removeItem('switching_user');
      alert(`切り替えに失敗しました: ${error.message}`);
      window.location.href = '/admin-login';
    }
  };

  return (
    <Box
      sx={{
        width: 240,
        height: '100vh',
        bgcolor: 'white',
        borderRight: '1px solid #e0e0e0',
        position: 'fixed',
        top: 0,
        left: 0,
        pt: '64px', // ヘッダーの高さ分のpadding
        overflowY: 'auto',
      }}
    >
      <List sx={{ pt: 2 }}>
        {/* 管理画面タブ */}
        <ListItemButton 
          onClick={() => handleTabClick('voice')}
          selected={location.pathname === '/admin' && currentTab === 'voice'}
        >
          <ListItemIcon>
            <MicIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary={getBoxTemplate(company?.industryType, 'voice').name} />
        </ListItemButton>
        <ListItemButton
          onClick={() => handleTabClick('thanks')}
          selected={location.pathname === '/admin' && currentTab === 'thanks'}
        >
          <ListItemIcon>
            <FavoriteBorderIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary={getBoxTemplate(company?.industryType, 'thanks').name} />
        </ListItemButton>
        <ListItemButton
          onClick={() => handleNavigation('/admin/theme-posts')}
          selected={location.pathname === '/admin/theme-posts'}
        >
          <ListItemIcon>
            <FolderIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary={getBoxTemplate(company?.industryType, 'theme').name} />
        </ListItemButton>

        {/* 投稿用のQRコード・URL（坂本様ご指摘④）。配るのは管理者全員の仕事なので、全員に出す */}
        <Divider sx={{ my: 1 }} />
        <ListItemButton
          onClick={() => handleNavigation('/admin/access-link')}
          selected={location.pathname === '/admin/access-link'}
        >
          <ListItemIcon>
            <QrCodeIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary="QRコード・URL" />
        </ListItemButton>
        
        {/* システム管理者メニュー（スーパー管理者のみ表示） */}
        {user?.userType === 'superadmin' && (
          <>
            <Divider sx={{ my: 1 }} />
            
            {/* 管理メニュー */}
            <ListItemButton 
              onClick={() => handleNavigation('/admin/user-management')}
              selected={location.pathname === '/admin/user-management'}
            >
              <ListItemIcon>
                <PeopleIcon sx={{ color: AppColors.primary }} />
              </ListItemIcon>
              <ListItemText primary="ユーザー管理" />
            </ListItemButton>
            <ListItemButton 
              onClick={() => handleNavigation('/admin/theme-management')}
              selected={location.pathname === '/admin/theme-management'}
            >
              <ListItemIcon>
                <MenuBookIcon sx={{ color: AppColors.primary }} />
              </ListItemIcon>
              <ListItemText primary="テーマ管理" />
            </ListItemButton>
            <ListItemButton 
              onClick={() => handleNavigation('/admin/company-management')}
              selected={location.pathname === '/admin/company-management'}
            >
              <ListItemIcon>
                <BusinessIcon sx={{ color: AppColors.primary }} />
              </ListItemIcon>
              <ListItemText primary="会社管理" />
            </ListItemButton>
            <ListItemButton 
              onClick={() => handleNavigation('/admin/audit-log')}
              selected={location.pathname === '/admin/audit-log'}
            >
              <ListItemIcon>
                <ArticleIcon sx={{ color: AppColors.primary }} />
              </ListItemIcon>
              <ListItemText primary="監査ログ" />
            </ListItemButton>
            <ListItemButton 
              onClick={() => handleNavigation('/admin/cancellation')}
              selected={location.pathname === '/admin/cancellation'}
            >
              <ListItemIcon>
                <CancelIcon sx={{ color: AppColors.primary }} />
              </ListItemIcon>
              <ListItemText primary="退会処理" />
            </ListItemButton>
          </>
        )}

        <Divider sx={{ my: 1 }} />
        
        {/* ホーム画面へ & 設定 */}
        <ListItemButton onClick={switchToCorporateUser}>
          <ListItemIcon>
            <HomeIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary="ホーム画面へ" />
        </ListItemButton>
        <ListItemButton 
          onClick={() => handleNavigation('/settings')}
          selected={location.pathname === '/settings'}
        >
          <ListItemIcon>
            <SettingsIcon sx={{ color: AppColors.primary }} />
          </ListItemIcon>
          <ListItemText primary="設定" />
        </ListItemButton>
      </List>
    </Box>
  );
};

